import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { EditorialTeam } from "@/components/editorial-team";
import { ReviewCasinoLogos } from "@/components/review-casino-logos";
import { EeatTrustBlocks } from "@/components/eeat-blocks";
import { CasinoList } from "@/components/casino-list";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/tonybet-uitbetaling";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/review/tonybet-uitbetaling";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/review/tonybet-uitbetaling")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "TonyBet Uitbetaling 2026: Hoe Lang Duurt het? Tijd & Limieten" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "TonyBet Uitbetaling 2026: Hoe Lang Duurt het? Tijd & Limieten" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("tonybet-uitbetaling"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            datePublished: "2026-10-17",
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "TonyBet uitbetaling", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: TonyBetPage,
});

const TIMELINE = [
  { t: "Aanvraag", d: "Account > betalingen > opnemen", v: "direct" },
  { t: "Controle", d: "Account, rekening op eigen naam, open bonus", v: "meestal < 24 uur" },
  { t: "Overboeking", d: "Geld naar je geverifieerde rekening", v: "na goedkeuring" },
  { t: "Je bank", d: "Bijschrijving op je rekening", v: "minuten tot 1 werkdag" },
];

function PayoutTimelineInfographic() {
  return (
    <figure id="infographic-tonybet-uitbetaling-tijd" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">TonyBet uitbetaling tijd: van aanvraag tot rekening</figcaption>
      <ol className="mt-5 grid gap-3 sm:grid-cols-4">
        {TIMELINE.map((s, i) => (
          <li key={s.t} className="rounded-lg border border-border bg-secondary/50 p-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
            <p className="mt-2 text-sm font-semibold text-foreground">{s.t}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.d}</p>
            <p className="mt-2 text-xs font-semibold text-success">{s.v}</p>
          </li>
        ))}
      </ol>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">Bronnen: Kansspelautoriteit, TonyBet NL en Nederlandse reviews, gecontroleerd 17 oktober 2026. Geen eigen meting.</p>
    </figure>
  );
}

const FACTS = [
  ["Vergunning", "KSA 1729 (TonyBet OÜ, geldig tot nov. 2027)"],
  ["Registratie", "Met iDIN via je bank"],
  ["Storten", "iDEAL / Wero, vanaf €10"],
  ["Opnemen", "Bankoverschrijving naar eigen rekening"],
  ["Verwerking", "Meestal binnen 24 uur"],
  ["Totale tijd", "Dezelfde dag tot 1 à 2 werkdagen"],
  ["Minimale opname", "€10 volgens reviews"],
  ["Eigen testdata", "Testronde gepland"],
];

function FactSheet() {
  return (
    <figure id="feiten-tonybet-uitbetaling" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">Feitenblad TonyBet uitbetaling</figcaption>
      <dl className="mt-4 divide-y divide-border">
        {FACTS.map(([k, v]) => (
          <div key={k} className="flex flex-wrap justify-between gap-2 py-2.5 text-sm">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-medium text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">Bronnen: KSA-register, tonybet.nl en Nederlandse reviews. Eigen metingen volgen in de volgende testronde.</p>
    </figure>
  );
}

function TonyBetPage() {
  const budget = new LinkBudget(PATH);
  const casinos = getPublicCasinos();

  const takeaways = [
    "Een TonyBet uitbetaling vraag je aan via betalingen > opnemen in je account.",
    "Het geld gaat per overboeking naar je eigen, geverifieerde bankrekening.",
    "Verwerking meestal binnen 24 uur; totaal dezelfde dag tot 1 à 2 werkdagen.",
    "Je kunt alleen opnemen naar een rekening op je eigen naam.",
    "Een volledig geverifieerd account is de grootste versneller.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">TonyBet uitbetaling</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        TonyBet uitbetaling: hoe lang het duurt, limieten en sneller je geld
      </h1>

      <ReviewCasinoLogos casinos={casinos} contextLabel="De top 5 aanbieders uit onze casinolijst" />

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">Casino's met een snelle uitbetaling in onze tests</h2>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
          </section>

          <div className="mt-10">
            <KeyTakeaways items={takeaways} />
          </div>

          <div className="mt-10">
            <FactSheet />
          </div>

          <div className="mt-10">
            <PayoutTimelineInfographic />
          </div>

          <section className="mt-12" aria-label="Alles over de TonyBet uitbetaling">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="TonyBet uitbetaling"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over de TonyBet uitbetaling
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="mt-12" aria-labelledby="bronnen">
            <h2 id="bronnen" className="text-xl font-semibold text-foreground">Bronnen</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {guide.sources.map((src) => (
                <li key={src.url}>
                  <a href={src.url} rel="noopener nofollow" target="_blank" className="text-primary underline underline-offset-4">{src.label}</a>
                </li>
              ))}
            </ul>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
